/*
  Product page directions — buy box + gallery behaviour.
  Works with Horizon's <product-form-component>: we only keep the form's
  hidden `id` input (and the fulfillment property) correct; the theme's own
  component posts to the cart and opens the drawer.
*/
(() => {
  const TZ = 'America/Denver';
  // Winter hours from 2026-10-01: Mon–Sat 10–7 · Sun 12–5, open every day (owner-confirmed 2026-10-15)
  const HOURS = { 0: '12–5', 1: '10–7', 2: '10–7', 3: '10–7', 4: '10–7', 5: '10–7', 6: '10–7' };

  const denverNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: TZ }));
  const fmtDay = (d) => d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });

  function shipWindow(min, max) {
    const a = denverNow(); a.setDate(a.getDate() + min);
    const b = denverNow(); b.setDate(b.getDate() + max);
    const sameMonth = a.getMonth() === b.getMonth();
    return `${fmtDay(a)}–${sameMonth ? String(b.getDate()).padStart(2, '0') : fmtDay(b)}`;
  }

  function todayHours() {
    const wd = denverNow().getDay();
    return HOURS[wd] ? `Open today ${HOURS[wd]}` : 'Open daily';
  }

  class PdpBuy extends HTMLElement {
    connectedCallback() {
      if (this.cfg) return;
      const raw = this.querySelector('script[data-pdp-config]');
      if (!raw) return;
      this.cfg = JSON.parse(raw.textContent);
      const c = this.cfg;
      this.money = new Intl.NumberFormat('en-US', { style: 'currency', currency: c.currency || 'USD' });
      this.state = {
        sel: c.initial.slice(),
        pkg: Boolean(c.pkg && c.pkgDefault),
        ful: c.ful === 'ship' ? 'ship' : 'pickup',
        up: -1,
        upSel: {},
        addons: new Set(),
      };
      // Horizon delegates submit from a document capture listener, so ours must sit
      // on window (capture runs window → document) to add extra lines before it fires.
      if (this.querySelector('[data-addon], script[data-pdp-upgrades]')) {
        window.addEventListener('submit', (e) => { if (this.contains(e.target)) this.onSubmit(e); }, true);
      }
      const upRaw = this.querySelector('script[data-pdp-upgrades]');
      this.up = upRaw ? JSON.parse(upRaw.textContent) : null;
      if (this.up) {
        this.up.waistMm = parseFloat(this.up.waist) || null;
        this.up.bindings.forEach((b) => {
          b.brakeIdx = b.options.findIndex((o) => /brake|width|size/i.test(o));
        });
        this.addEventListener('change', (e) => this.onUpChange(e));
      }
      if (this.state.pkg) this.ensurePkgSize();
      // Admin order is arbitrary (Sender: 176, 184, 191, 168) — sort numeric sizes.
      this.querySelectorAll('.pdp-chips[data-sort]').forEach((wrap) => {
        const chips = [...wrap.children];
        if (chips.every((b) => !Number.isNaN(parseFloat(b.dataset.val)))) {
          chips.sort((x, y) => parseFloat(x.dataset.val) - parseFloat(y.dataset.val)).forEach((b) => wrap.appendChild(b));
        }
      });
      this.addEventListener('click', (e) => this.onClick(e));
      this.querySelectorAll('[data-pdp-ship]').forEach((el) => { el.textContent = shipWindow(c.shipMin, c.shipMax); });
      this.render();
    }

    get sizeIdx() { return this.cfg.sizeIndex; }

    pkgVariantFor(size) {
      const p = this.cfg.pkg;
      return p ? p.variants.find((v) => v.options[p.sizeIndex] === size) : null;
    }

    mainVariantFor(sel) {
      return this.cfg.main.variants.find((v) => v.options.every((o, i) => o === sel[i]));
    }

    current() {
      const { sel, pkg } = this.state;
      if (pkg && this.sizeIdx > -1) return this.pkgVariantFor(sel[this.sizeIdx]) || null;
      return this.mainVariantFor(sel) || null;
    }

    isAvailable(optIdx, val) {
      const sel = this.state.sel.slice();
      sel[optIdx] = val;
      if (this.state.pkg && optIdx === this.sizeIdx) {
        const v = this.pkgVariantFor(val);
        return Boolean(v && v.available);
      }
      const v = this.mainVariantFor(sel);
      return Boolean(v && v.available);
    }

    ensurePkgSize() {
      const i = this.sizeIdx;
      if (i < 0) return;
      const ok = this.pkgVariantFor(this.state.sel[i]);
      if (ok && ok.available) return;
      const first = this.cfg.pkg.variants.find((v) => v.available);
      if (first) this.state.sel[i] = first.options[this.cfg.pkg.sizeIndex];
    }

    ensureMainSel() {
      const v = this.mainVariantFor(this.state.sel);
      if (v && v.available) return;
      const first = this.cfg.main.variants.find((x) => x.available);
      if (first) this.state.sel = first.options.slice();
    }

    // ---- Upgrade bindings ("move up, get more") ----
    brakesFor(b) {
      if (b.brakeIdx < 0) return [];
      const vals = [...new Set(b.variants.map((v) => v.options[b.brakeIdx]))];
      return vals.sort((x, y) => parseFloat(x) - parseFloat(y));
    }

    // Smallest brake at least as wide as the waist. null = too narrow for this ski.
    fitBrake(b) {
      const w = this.up.waistMm;
      if (!w || b.brakeIdx < 0) return undefined;
      return this.brakesFor(b).find((x) => parseFloat(x) >= w && b.variants.some((v) => v.available && v.options[b.brakeIdx] === x)) || null;
    }

    upVariant(i) {
      const b = this.up.bindings[i];
      const brake = this.fitBrake(b);
      if (brake === null) return null;
      const pick = this.state.upSel[i] || {};
      const ok = b.variants.filter((v) => v.available
        && (brake === undefined || v.options[b.brakeIdx] === brake)
        && Object.entries(pick).every(([k, val]) => v.options[k] === val));
      return ok[0] || b.variants.find((v) => v.available && (brake === undefined || v.options[b.brakeIdx] === brake)) || null;
    }

    renderUpOpts(i, box) {
      const b = this.up.bindings[i];
      const brake = this.fitBrake(b);
      const cur = this.upVariant(i);
      const esc = (x) => String(x).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
      box.innerHTML = b.options.map((name, k) => {
        if (k === b.brakeIdx && brake !== undefined) return '';
        const vals = [...new Set(b.variants.filter((v) => v.available && (brake === undefined || k === b.brakeIdx || v.options[b.brakeIdx] === brake)).map((v) => v.options[k]))];
        if (vals.length < 2) return '';
        return `<label class="pdp-up__opt"><span>${esc(name)}</span><select data-up-opt="${i}:${k}">${vals.map((v) => `<option${cur && cur.options[k] === v ? ' selected' : ''}>${esc(v)}</option>`).join('')}</select></label>`;
      }).join('');
    }

    onUpChange(e) {
      const t = e.target.closest('[data-up-opt]');
      if (!t) return;
      const [i, k] = t.dataset.upOpt.split(':').map(Number);
      this.state.upSel[i] = { ...(this.state.upSel[i] || {}), [k]: t.value };
      this.render();
    }

    // Extra lines (upgrade binding, paid add-ons) go in first; then Horizon's own
    // submit adds the main product and opens the drawer.
    async onSubmit(e) {
      if (this.upAdding) return;
      const size = this.sizeIdx > -1 ? this.state.sel[this.sizeIdx] : '';
      const forLine = [this.cfg.title, size].filter(Boolean).join(' · ');
      const items = [];
      if (this.up && this.state.up > -1) {
        const bv = this.upVariant(this.state.up);
        if (!bv) { e.preventDefault(); e.stopImmediatePropagation(); return; }
        items.push({ id: bv.id, quantity: 1, properties: { 'Mount on': forLine, Bonus: this.up.note } });
      }
      this.state.addons.forEach((id) => items.push({ id: Number(id), quantity: 1, properties: { For: forLine } }));
      if (!items.length) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const form = e.target;
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error((await res.json()).description || 'Binding add failed');
      } catch (err) {
        console.error(err);
        this.querySelectorAll('[data-pdp="stock"]').forEach((el) => { el.textContent = "COULDN'T ADD TO CART — CALL US"; });
        return;
      }
      this.upAdding = true;
      try { form.requestSubmit(); } finally { this.upAdding = false; }
    }

    onClick(e) {
      const t = e.target.closest('[data-opt], [data-ful], [data-pkg-toggle], [data-pkg-set], [data-up], [data-addon]');
      if (!t || !this.contains(t)) return;
      if (t.hasAttribute('data-opt')) {
        const i = Number(t.dataset.opt);
        if (!this.isAvailable(i, t.dataset.val)) return;
        this.state.sel[i] = t.dataset.val;
      } else if (t.hasAttribute('data-ful')) {
        this.state.ful = t.dataset.ful;
      } else if (t.hasAttribute('data-pkg-toggle')) {
        this.state.pkg = !this.state.pkg;
      } else if (t.hasAttribute('data-pkg-set')) {
        this.state.pkg = t.dataset.pkgSet === 'on';
      } else if (t.hasAttribute('data-addon')) {
        const id = t.dataset.addon;
        if (this.state.addons.has(id)) this.state.addons.delete(id); else this.state.addons.add(id);
      } else if (t.hasAttribute('data-up')) {
        const i = Number(t.dataset.up);
        if (t.getAttribute('aria-disabled') === 'true') return;
        this.state.up = this.state.up === i ? -1 : i;
        if (this.state.up > -1) this.state.pkg = false;
      }
      if (this.state.pkg) this.state.up = -1;
      if (this.state.pkg) this.ensurePkgSize(); else this.ensureMainSel();
      e.preventDefault();
      this.render();
    }

    stockLine(v, size) {
      const c = this.cfg;
      const where = size ? ` IN ${size}` : '';
      const tail = this.state.pkg ? ' · PACKAGE' : (this.state.up > -1 ? ' · + BINDING' : '');
      if (!v || !v.available) return `SOLD OUT${where}`;
      if (v.qty === 1) return `LAST ONE${where}${tail}`;
      if (typeof v.qty === 'number' && v.qty > 1 && v.qty <= c.lowStock) return `${v.qty} LEFT${where}${tail}`;
      return `IN STOCK${where}${tail}`;
    }

    steps() {
      const s = [{ t: 'Ordered', d: 'Today' }];
      if (this.state.pkg || this.state.up > -1) s.push({ t: 'Mounted + DIN-set', d: 'On our bench' });
      if (this.state.ful === 'pickup') s.push({ t: 'Ready on Walnut St', d: "We email you when it's ready" });
      else s.push({ t: 'Shipped', d: 'From Boulder' }, { t: 'Delivered', d: `Est. ${shipWindow(this.cfg.shipMin, this.cfg.shipMax)}` });
      return s.map((x, i) => ({ ...x, n: `0${i + 1}` }));
    }

    render() {
      const { sel, pkg, ful } = this.state;
      const c = this.cfg;
      const v = this.current();
      const size = this.sizeIdx > -1 ? sel[this.sizeIdx] : '';
      const esc = (s) => String(s).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

      this.querySelectorAll('[data-opt]').forEach((b) => {
        const i = Number(b.dataset.opt);
        const on = sel[i] === b.dataset.val;
        const out = !this.isAvailable(i, b.dataset.val);
        b.classList.toggle('is-on', on && !out);
        b.classList.toggle('is-out', out);
        b.setAttribute('aria-pressed', String(on && !out));
        b.setAttribute('aria-disabled', String(out));
      });
      this.querySelectorAll('[data-ful]').forEach((b) => {
        const on = b.dataset.ful === ful;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', String(on));
      });
      this.querySelectorAll('[data-pkg-toggle]').forEach((b) => {
        b.classList.toggle('is-on', pkg);
        b.setAttribute('aria-pressed', String(pkg));
      });
      this.querySelectorAll('[data-addon]').forEach((b) => {
        const on = this.state.addons.has(b.dataset.addon);
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', String(on));
      });
      this.querySelectorAll('[data-pkg-set]').forEach((b) => {
        const on = (b.dataset.pkgSet === 'on') === pkg;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', String(on));
      });

      const up = this.up && this.state.up > -1 ? this.state.up : -1;
      const upV = up > -1 ? this.upVariant(up) : null;
      if (this.up) {
        this.querySelectorAll('[data-up]').forEach((row) => {
          const i = Number(row.dataset.up);
          const b = this.up.bindings[i];
          const brake = this.fitBrake(b);
          const any = this.upVariant(i);
          const fit = row.querySelector('[data-up-fit]');
          const out = !any;
          let msg = '';
          if (brake === null) {
            const max = this.brakesFor(b).filter((x) => b.variants.some((vv) => vv.available && vv.options[b.brakeIdx] === x)).pop();
            msg = max ? `Brakes max ${parseFloat(max)} mm — too narrow for this ${this.up.waistMm} mm ski` : 'Sold out';
          } else if (out) msg = 'Sold out';
          else if (brake) msg = `${parseFloat(brake)} mm brake · matched to the ${this.up.waistMm} mm waist`;
          if (fit) fit.textContent = msg;
          row.classList.toggle('is-on', i === up);
          row.classList.toggle('is-out', out);
          row.setAttribute('aria-pressed', String(i === up));
          row.setAttribute('aria-disabled', String(out));
          const box = this.querySelector(`[data-up-opts="${i}"]`);
          if (box) { box.hidden = i !== up; if (i === up) this.renderUpOpts(i, box); }
          const more = this.querySelector(`[data-up-more="${i}"]`);
          if (more) more.hidden = i !== up;
        });
        const upProp = this.querySelector('[data-pdp="prop-up"]');
        if (upProp) {
          upProp.value = up > -1 ? `${this.up.note} — with ${this.up.bindings[up].title}` : '';
          upProp.disabled = up < 0;
        }
      }
      const total = v ? v.price + (upV ? upV.price : 0) : 0;
      const price = v ? this.money.format(total / 100) : '';
      this.querySelectorAll('[data-pdp="price"], [data-pdp="btn-price"]').forEach((el) => { el.textContent = price; });
      this.querySelectorAll('[data-pdp="save"]').forEach((el) => { el.hidden = !pkg; });
      this.querySelectorAll('[data-pdp="size"]').forEach((el) => { el.textContent = size; });
      this.querySelectorAll('[data-pdp="stock"]').forEach((el) => { el.textContent = this.stockLine(v, size); });

      this.querySelectorAll('[data-pdp-steps]').forEach((el) => {
        const style = el.dataset.pdpSteps;
        el.innerHTML = this.steps().map((s) => (style === 'inline'
          ? `<span class="pdp-step"><span class="pdp-step__n">${s.n}</span> <span class="pdp-step__t pdp-u">${esc(s.t)}</span></span>`
          : `<div class="pdp-step"><span class="pdp-step__n">${s.n}</span><span class="pdp-step__t pdp-u">${esc(s.t)}</span>${style === 'rail' && el.dataset.compact ? '' : `<span class="pdp-step__d">${esc(s.d)}</span>`}</div>`)).join('');
      });

      const idInput = this.querySelector('input[name="id"]');
      if (idInput && v) idInput.value = v.id;
      const prop = this.querySelector('[data-pdp="prop-ful"]');
      if (prop) prop.value = ful === 'pickup' ? c.labels.propPickup : c.labels.propShip;
      // Claimed offers ride on the cart line as one property; disabled = not posted.
      const offerProp = this.querySelector('[data-pdp="prop-offer"]');
      if (offerProp) {
        const names = (offerProp.dataset.alwaysNames + (pkg ? offerProp.dataset.pkgNames : '')).split('|').filter(Boolean);
        offerProp.value = names.join(' + ');
        offerProp.disabled = names.length === 0;
      }

      const canBuy = Boolean(v && v.available && (up < 0 || upV));
      this.querySelectorAll('[data-pdp="atc"]').forEach((btn) => {
        btn.disabled = !canBuy;
        const label = btn.querySelector('[data-pdp="cta"]');
        if (!label) return;
        label.textContent = !canBuy ? label.dataset.soldout : (ful === 'ship' && label.dataset.ship) ? label.dataset.ship : label.dataset.pickup;
      });

      if (!pkg && v && c.updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState(window.history.state, '', url.toString());
      }

      if (c.holdBar && size) {
        const bar = document.querySelector('.local-call-bar__call span');
        if (bar) bar.textContent = `Call to hold the ${size}`;
      }
    }
  }

  class PdpGallery extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      const main = this.querySelector('[data-gal-main]');
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-thumb]');
        if (!t || !main) return;
        main.src = t.dataset.src;
        main.srcset = t.dataset.srcset || '';
        main.alt = t.dataset.alt || '';
        this.querySelectorAll('[data-thumb]').forEach((x) => x.classList.toggle('is-on', x === t));
      });
      const strip = this.querySelector('.pdp-gal__strip');
      const count = this.querySelector('[data-gal-count]');
      if (strip && count) {
        strip.addEventListener('scroll', () => {
          const i = Math.round(strip.scrollLeft / Math.max(strip.clientWidth, 1));
          count.textContent = `${i + 1} / ${strip.children.length}`;
        }, { passive: true });
      }
    }
  }

  const hydrateToday = (root = document) => {
    root.querySelectorAll('[data-pdp-today]').forEach((el) => { el.textContent = todayHours(); });
  };

  if (!customElements.get('pdp-buy')) customElements.define('pdp-buy', PdpBuy);
  if (!customElements.get('pdp-gallery')) customElements.define('pdp-gallery', PdpGallery);
  hydrateToday();
  document.addEventListener('shopify:section:load', (e) => hydrateToday(e.target));
})();

